import { useState, useRef, useEffect, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, ArrowRight } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';

// ── Response shape (mirrors GET /api/search) ────────────────────────────────────

interface EntityHit {
  id: number;
  name: string;
  code: string | null;
}
interface ItemHit extends EntityHit {
  category: string;
}
interface TxnHit {
  id: number;
  number: string;
  party: string | null;
  status: string;
}

interface SearchResult {
  customers: EntityHit[];
  customers_total: number;
  suppliers: EntityHit[];
  suppliers_total: number;
  items: ItemHit[];
  items_total: number;
  estimates: TxnHit[];
  estimates_total: number;
  purchase_orders: TxnHit[];
  purchase_orders_total: number;
  sales_returns: TxnHit[];
  sales_returns_total: number;
  purchase_returns: TxnHit[];
  purchase_returns_total: number;
  countertop_jobs: TxnHit[];
  countertop_jobs_total: number;
}

interface SearchResponse {
  data: SearchResult;
}

// A flattened, render-ready row shared by every category.
interface Row {
  key: string | number;
  to: string;
  primary: string;
  secondary?: string;
}
interface Group {
  label: string;
  total: number;
  rows: Row[];
}

const ROWS_PER_GROUP = 3;

// Builds the ordered list of non-empty category groups from the raw response.
function buildGroups(r: SearchResult): Group[] {
  const entityRows = (hits: EntityHit[], base: string): Row[] =>
    hits.slice(0, ROWS_PER_GROUP).map((h) => ({
      key: h.id,
      to: `${base}/${h.id}`,
      primary: h.name,
      secondary: h.code ?? undefined,
    }));
  const txnRows = (hits: TxnHit[], base: string): Row[] =>
    hits.slice(0, ROWS_PER_GROUP).map((h) => ({
      key: h.id,
      to: `${base}/${h.id}`,
      primary: h.number,
      secondary: h.party ?? undefined,
    }));

  const groups: Group[] = [
    { label: 'Customers', total: r.customers_total, rows: entityRows(r.customers, '/customers') },
    { label: 'Suppliers', total: r.suppliers_total, rows: entityRows(r.suppliers, '/suppliers') },
    {
      label: 'Items',
      total: r.items_total,
      rows: r.items.slice(0, ROWS_PER_GROUP).map((i) => ({
        key: i.id,
        to: `/items/${i.id}`,
        primary: i.name,
        secondary: i.category,
      })),
    },
    { label: 'Estimates', total: r.estimates_total, rows: txnRows(r.estimates, '/estimates') },
    {
      label: 'Purchase Orders',
      total: r.purchase_orders_total,
      rows: txnRows(r.purchase_orders, '/purchase-orders'),
    },
    {
      label: 'Sales Returns',
      total: r.sales_returns_total,
      rows: txnRows(r.sales_returns, '/returns'),
    },
    {
      label: 'Purchase Returns',
      total: r.purchase_returns_total,
      rows: txnRows(r.purchase_returns, '/purchase-returns'),
    },
    {
      label: 'Countertop Jobs',
      total: r.countertop_jobs_total,
      rows: txnRows(r.countertop_jobs, '/countertop-jobs'),
    },
  ];
  return groups.filter((g) => g.total > 0);
}

// ── GlobalSearchBox ─────────────────────────────────────────────────────────────

export default function GlobalSearchBox() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = q.trim();

  // Debounce the trimmed value ~250ms; the query keys off this so keystrokes
  // don't each fire a request.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(trimmed), 250);
    return () => clearTimeout(t);
  }, [trimmed]);

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: () => apiFetch<SearchResponse>(`/api/search?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 2,
  });

  // Close on outside pointer / Escape while open (mirrors the UserMenu pattern).
  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function goToResults(e: FormEvent) {
    e.preventDefault();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    resetAndClose();
  }

  function resetAndClose() {
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
  }

  // Results only count once the debounced term (which they key off) is valid.
  const results = debounced.length >= 2 ? data?.data : undefined;
  const groups = results ? buildGroups(results) : [];
  // "Pending" = still waiting for the debounce to settle or the request to land.
  const loading = trimmed.length >= 2 && (isFetching || debounced !== trimmed);
  const showPanel = open && trimmed.length >= 2;
  const noResults = !loading && results != null && groups.length === 0;

  return (
    <form
      ref={wrapRef}
      onSubmit={goToResults}
      className="relative flex items-center w-full max-w-xs"
    >
      <Search
        className="absolute left-2.5 h-4 w-4 text-muted-foreground pointer-events-none"
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        type="search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search…"
        autoComplete="off"
        aria-label="Search"
        className={cn(
          'h-9 w-full pl-8 pr-8 rounded-md border border-input bg-background text-sm',
          'placeholder:text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          'transition-colors',
        )}
      />
      {loading && (
        <Loader2
          className="absolute right-2.5 h-4 w-4 text-muted-foreground animate-spin pointer-events-none"
          aria-hidden="true"
        />
      )}

      {showPanel && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-50 max-h-[70vh] overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg">
          {groups.map((group) => (
            <div key={group.label} className="py-1">
              <div className="flex items-center justify-between px-3 pt-1.5 pb-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </span>
                <span className="text-[10px] font-medium text-muted-foreground tabular-nums">
                  {group.total}
                </span>
              </div>
              {group.rows.map((row) => (
                <Link
                  key={`${group.label}-${row.key}`}
                  to={row.to}
                  onClick={resetAndClose}
                  className="flex items-center justify-between gap-2 mx-1 px-2 py-1.5 rounded-md hover:bg-accent transition-colors"
                >
                  <span className="truncate text-sm font-medium">{row.primary}</span>
                  {row.secondary && (
                    <span className="shrink-0 max-w-[45%] truncate text-xs text-muted-foreground">
                      {row.secondary}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          ))}

          {loading && groups.length === 0 && (
            <p className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Searching…
            </p>
          )}
          {noResults && (
            <p className="px-3 py-2.5 text-sm text-muted-foreground">
              No results for &ldquo;{trimmed}&rdquo;.
            </p>
          )}

          <button
            type="submit"
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-primary border-t border-border hover:bg-accent transition-colors"
          >
            <span className="truncate">See all results for &ldquo;{trimmed}&rdquo;</span>
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
          </button>
        </div>
      )}
    </form>
  );
}
